/**
 * 「지금 볼 채널」 판정 (`docs/23` §7.2). **부팅 시 한 번.**
 *
 * **순수 함수로 뗀다** — 이 패키지는 node 환경에서 테스트한다(jsdom 미도입). `location` 이나
 * `localStorage` 를 직접 만지면 이 규칙이 검사에서 통째로 빠진다 (`routing.ts`·`resolveLocale`
 * 과 같은 이유).
 *
 * **`/` 는 여전히 시청 화면이다** (D8). 바뀌는 것은 「어느 채널이냐」뿐이고, 그래서 이
 * 함수는 화면 이름이 아니라 **재생할지·목록을 그릴지**를 답한다. `/` 를 목록으로 바꾸면
 * 하네스 일곱이 `/?debug=1` 에서 재생을 시작해 재는 `T_freeze` 가 통째로 사라진다.
 */

import type { ChannelSummary } from '@apm/contracts';

export type ChannelChoice =
  /** 이 채널을 바로 튼다 */
  | { kind: 'play'; channelId: string }
  /** 고르게 한다. 공개 채널이 둘 이상이다 */
  | { kind: 'list' }
  /** 재생 가능한 채널이 하나도 없다 */
  | { kind: 'empty' };

export interface ChannelSelectionInput {
  /**
   * `?channel=` 또는 `VITE_CHANNEL_ID`. **저장하지 않는다** — 하네스와 데모 운영자가
   * 잠시 고정해 보는 통로다 (`?lang=` 과 같은 방침).
   */
  readonly preferred?: string | null;
  /** 사람이 고른 값 (`localStorage['bloom.channelId']`) */
  readonly saved?: string | null;
  /** `GET /v1/channels` — **재생 가능한 항목이 있는 채널만** 들어 있다 (절대 규칙 1 의 승계) */
  readonly channels: readonly ChannelSummary[];
}

/**
 * ```
 * 1) preferred 가 공개 목록에 있으면      → 그 채널
 * 2) saved 가 공개 목록에 있으면          → 그 채널
 * 3) 공개 채널이 1개                      → 그 채널
 * 4) 공개 채널이 2개 이상                 → 목록
 * 5) 0개                                  → 안내
 * ```
 *
 * **1)2) 는 「목록에 있으면」이다.** 저장값이나 환경변수가 가리키는 채널이 편성이 비어
 * 공개 목록에서 빠졌거나 아예 삭제됐을 수 있다 — 그대로 틀면 **빈 화면**이다.
 * 그때는 그 값을 버리고 아래로 내려간다 (`docs/23` §7.2).
 *
 * **목록이 비면 `preferred` 도 쓰지 않는다.** 서버가 「재생 가능한 채널이 없다」고 답한
 * 것이고, 그 상태에서 어느 채널을 틀어도 결과는 빈 플레이리스트다.
 *
 * 맨 앞의 조기 반환은 **중복 방어다** — 목록이 비면 아래 `has()` 의 `some()` 이 이미
 * 거짓이라 그것 없이도 결과가 같다(변이로 확인했다). 의도를 드러내려고 남긴다.
 */
export function resolveChannel(input: ChannelSelectionInput): ChannelChoice {
  const { channels } = input;
  if (channels.length === 0) return { kind: 'empty' };

  const has = (id: string | null | undefined): id is string =>
    typeof id === 'string' && id !== '' && channels.some((c) => c.channelId === id);

  if (has(input.preferred)) return { kind: 'play', channelId: input.preferred };
  if (has(input.saved)) return { kind: 'play', channelId: input.saved };

  const only = channels[0];
  if (channels.length === 1 && only !== undefined)
    return { kind: 'play', channelId: only.channelId };

  return { kind: 'list' };
}
